/**
 * Contact / Group info (06 S9/S13) — opened as a right-side SlideOver. Large
 * avatar, name/about or subject, member list (group), Encryption row, and
 * destructive actions (Block/Exit). Driven by uiStore.infoForConv.
 */
import { ShieldCheck, Bell, Trash2, Ban, LogOut, UserPlus } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { lastSeenText } from '../../lib/format';
import { Avatar } from '../primitives/Avatar';
import { SlideOver } from '../primitives/Overlays';
import { SettingsRow } from '../settings/SettingsControls';
import styles from './InfoPanel.module.css';

export function InfoPanel() {
  const cid = useUiStore((s) => s.infoForConv);
  const closeInfo = useUiStore((s) => s.closeInfo);
  const toast = useUiStore((s) => s.toast);
  const conv = useChatStore((s) => (cid ? s.getConversation(cid) : undefined));
  const userById = useChatStore((s) => s.userById);
  const me = useChatStore((s) => s.me);
  const presence = useChatStore((s) => s.presence);

  const open = Boolean(cid && conv);
  if (!conv) return <SlideOver open={open} onClose={closeInfo} title="Info"><div /></SlideOver>;

  const isGroup = conv.type === 'group';
  const peer = !isGroup && conv.peerId ? userById(conv.peerId) : undefined;
  const title = isGroup ? conv.title ?? 'Group' : peer?.displayName ?? 'Unknown';
  const avatarId = isGroup ? conv.id : peer?.id ?? conv.id;
  const subtitle = isGroup
    ? `Group · ${conv.members.length} members`
    : peer
      ? lastSeenText(presence[peer.id]?.state ?? peer.presence, presence[peer.id]?.lastSeenAtMs ?? peer.lastSeenAtMs)
      : '';

  return (
    <SlideOver open={open} onClose={closeInfo} title={isGroup ? 'Group info' : 'Contact info'}>
      <div className={styles.hero}>
        <Avatar id={avatarId} name={title} url={conv.avatarUrl} size={160} />
        <h2 className={styles.name}>{title}</h2>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      {!isGroup && peer && (
        <div className={styles.section}>
          <span className={styles.sectionLabel}>About</span>
          <p className={styles.about}>{peer.about}</p>
        </div>
      )}

      {isGroup && (
        <div className={styles.members}>
          <div className={styles.membersHeader}>
            <span className={styles.sectionLabel}>{conv.members.length} members</span>
            {conv.myRole === 'admin' && (
              <button className={styles.addBtn} onClick={() => toast('Add members (demo)')} aria-label="Add members">
                <UserPlus size={18} /> Add
              </button>
            )}
          </div>
          <ul>
            {conv.members.map((m) => {
              const u = userById(m.userId);
              const isMe = m.userId === me.id;
              return (
                <li key={m.userId} className={styles.member}>
                  <Avatar id={m.userId} name={u?.displayName ?? '?'} size={40} />
                  <span className={styles.memberName}>
                    {isMe ? 'You' : u?.displayName ?? 'Unknown'}
                  </span>
                  {m.role === 'admin' && <span className={styles.adminTag}>Admin</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className={styles.rows}>
        <SettingsRow icon={<Bell size={20} />} label="Mute notifications" onClick={() => toast('Muted')} />
        <SettingsRow icon={<ShieldCheck size={20} />} label="Encryption" value="Messages are end-to-end encrypted. Tap to verify." onClick={() => toast('Open security code')} />
      </div>

      <div className={styles.danger}>
        {isGroup ? (
          <SettingsRow icon={<LogOut size={20} />} label="Exit group" onClick={() => { toast('Left group'); closeInfo(); }} />
        ) : (
          <SettingsRow icon={<Ban size={20} />} label={`Block ${title}`} onClick={() => toast('Blocked')} />
        )}
        <SettingsRow icon={<Trash2 size={20} />} label="Delete chat" onClick={() => { toast('Chat deleted'); closeInfo(); }} />
      </div>
    </SlideOver>
  );
}
